/**
 * Onboarding tour context.
 *
 * Holds the single piece of cross-cutting state the guided product walkthrough
 * needs — whether it is currently running — and exposes `startTour()` so any
 * surface (Settings, the top-bar help button) can replay it on demand.
 *
 * First-login gating lives here: a localStorage flag (`solandio:tour-done`)
 * records that the user has already seen (or skipped) the tour. The flag is
 * read once on mount; the auto-start decision is left to the consumer (the
 * AppShell), which only mounts this provider once a real family shell is on
 * screen — never on sign-in or the no-family onboarding screen.
 *
 * No UI lives here. The <OnboardingTour> component renders react-joyride and
 * reads `run` / `endTour` from this context.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const TOUR_DONE_KEY = 'solandio:tour-done';

/** Best-effort read of the "already completed" flag (localStorage can throw). */
function readDone(): boolean {
  try {
    return window.localStorage.getItem(TOUR_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Best-effort persist of the "already completed" flag. */
function writeDone(): void {
  try {
    window.localStorage.setItem(TOUR_DONE_KEY, '1');
  } catch {
    /* ignore — private mode / disabled storage */
  }
}

interface TourContextValue {
  /** Whether the tour is currently running. */
  run: boolean;
  /** Whether the user has completed/skipped the tour before (first-login gate). */
  hasSeen: boolean;
  /** Start (or replay) the tour. Ignores the first-login flag. */
  startTour: () => void;
  /**
   * End the tour. Pass `markSeen` (default true) to persist the done flag so it
   * won't auto-start again. Replays that the user dismisses also mark it seen.
   */
  endTour: (markSeen?: boolean) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const [run, setRun] = useState(false);
  const [hasSeen, setHasSeen] = useState<boolean>(() => readDone());

  const startTour = useCallback(() => {
    setRun(true);
  }, []);

  const endTour = useCallback((markSeen = true) => {
    setRun(false);
    if (markSeen) {
      writeDone();
      setHasSeen(true);
    }
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({ run, hasSeen, startTour, endTour }),
    [run, hasSeen, startTour, endTour],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within <TourProvider>.');
  return ctx;
}
